import React, { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/command';
import { COUNTRY_DIAL_CODES } from '../../lib/countryDialCodes';
import { cn } from '@/lib/utils';

/**
 * CountryDialPicker — searchable shadcn-Command popover for picking a
 * country dial code (used on /signup next to the phone input).
 *
 * Replaces the previous native <select> which forced users to scroll
 * through 55 options on mobile. Now users can type "Phil" and instantly
 * narrow to Philippines, or "+44" to jump to UK.
 *
 * Props:
 *   value           — current dial code, e.g. "+1"
 *   onChange(dial)  — called with the new dial code when user picks
 *   testId          — data-testid for the trigger button (default: "country-dial-picker")
 */
const CountryDialPicker = ({ value, onChange, testId = 'country-dial-picker' }) => {
  const [open, setOpen] = useState(false);

  // We may have multiple countries sharing the same dial code (e.g. US + CA
  // both = +1). Match by dial first; if multiple share it, prefer US for
  // display. The actual stored value is just the dial string.
  const selected =
    COUNTRY_DIAL_CODES.find((c) => c.dial === value && c.code === 'US') ||
    COUNTRY_DIAL_CODES.find((c) => c.dial === value) ||
    COUNTRY_DIAL_CODES[0];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={`Country code: ${selected.label}`}
          className="h-10 min-w-[8rem] justify-between bg-zinc-900 border-white/10 text-white hover:bg-zinc-800 hover:text-white px-3"
          data-testid={testId}
        >
          <span className="flex items-center gap-1.5 truncate">
            <span aria-hidden="true">{selected.flag}</span>
            <span className="font-medium">{selected.dial}</span>
          </span>
          <ChevronsUpDown className="ml-1 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[280px] p-0 bg-zinc-900 border-white/10"
        align="start"
        data-testid="country-dial-picker-popover"
      >
        <Command className="bg-zinc-900">
          <CommandInput
            placeholder="Search country or +code…"
            className="text-white placeholder:text-gray-500"
            data-testid="country-dial-search"
          />
          <CommandList>
            <CommandEmpty className="py-3 text-center text-sm text-gray-400">
              No country found.
            </CommandEmpty>
            <CommandGroup>
              {COUNTRY_DIAL_CODES.map((c) => (
                <CommandItem
                  key={`${c.code}-${c.dial}`}
                  // `value` is what cmdk filters on — combining label, dial,
                  // and ISO code lets users type "Phil" OR "+63" OR "PH".
                  value={`${c.label} ${c.dial} ${c.code}`}
                  onSelect={() => {
                    onChange(c.dial);
                    setOpen(false);
                  }}
                  className="text-white aria-selected:bg-white/10 cursor-pointer"
                  data-testid={`country-dial-option-${c.code}`}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      selected.dial === c.dial && selected.code === c.code
                        ? 'opacity-100 text-fuchsia-400'
                        : 'opacity-0'
                    )}
                  />
                  <span className="mr-2 text-lg leading-none">{c.flag}</span>
                  <span className="flex-1 truncate">{c.label}</span>
                  <span className="ml-2 text-gray-500 text-sm tabular-nums">{c.dial}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default CountryDialPicker;
